import { atom } from '@reatom/framework'
import { useAtom } from '@reatom/npm-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { selectedNetworkAtom } from '@root/entities/network'
import { ProfileBoardStatisticsHeader } from '@root/features/profile/board-statistics/ui/profile-board-statistics-header'
import { StatisticsTables } from '@root/features/profile/board-statistics/ui/profile-board-statistics-tables'
import { DistributeDrawerMobile } from '@root/features/profile/distribute-drawer'
import {
  reatomWalletsTradingAnalytics,
  useOwnProfileWallets,
} from '@root/features/services/wallet-profile'
import { ProfileOwner } from '@root/features/user/profile/model/profile-owner'
import { MyProfileHeader } from '@root/features/user/profile-header/ui/mobile/my-profile-header'
import { UserProfileHeader } from '@root/features/user/profile-header/ui/mobile/user-profile-header'
import { reatomUserTradingAnalytics } from '@root/features/user/user-trading-analytics/atoms'
import { AnalysisBlock } from '@root/features/user/user-trading-analytics/ui/analysis/analysis-block'
import { AvgBuyMcDistributionBlock } from '@root/features/user/user-trading-analytics/ui/avg-buy-mc-distribution/avg-buy-mc-distribution-block'
import { DistributionBlock } from '@root/features/user/user-trading-analytics/ui/distribution/distribution-block'
import { PhishingCheckBlock } from '@root/features/user/user-trading-analytics/ui/fishing-checks/fishing-checks-block'
import { UserProfilePnl } from '@root/features/user/user-trading-analytics/ui/pnl/user-profile-pnl'
import { FadeOverflow, UnderlineTabs } from '@root/shared/ui-kit'

import type { ProfileWallet } from '@root/entities/profile/model/profile-wallet'
import type { DelugeId } from '@root/entities/user-short'

type ProfileStatisticsProps = {
  delugeId: DelugeId
  wallets: ReadonlyArray<ProfileWallet>
  profileOwner?: ProfileOwner
}

type ProfileStatisticsTab = 'phishing' | 'distribution' | 'profit'

export const ProfileStatistics: React.FC<ProfileStatisticsProps> = ({
  delugeId,
  wallets,
  profileOwner = ProfileOwner.AnotherUsers,
}) => {
  const { t } = useTranslation()

  const [tab, setTab] = useState<ProfileStatisticsTab>('phishing')
  // The account's own wallets, because the list that used to arrive with the
  // profile came from a route that was never ported and was always empty.
  const ownWallets = useOwnProfileWallets()
  const [network] = useAtom(selectedNetworkAtom)

  const model = useMemo(
    () =>
      reatomUserTradingAnalytics(
        {
          delugeId,
          wallets: ownWallets,
          // Built from whatever the selector has resolved — one wallet or all
          // of them — so the figures are a real read rather than an error.
          source: (selected) =>
            // eslint-disable-next-line @reatom/unit-naming-rule
            reatomWalletsTradingAnalytics({
              network,
              walletsAtom: atom(
                (ctx) => ctx.spy(selected).map((wallet) => wallet.address),
                'walletsAtom',
              ),
            }),
        },
        'model',
      ),
    [delugeId, ownWallets, network],
  )
  const [period] = useAtom(model.periodAtom)

  const renderScreen = () =>
    match(tab)
      .with('phishing', () => <PhishingCheckBlock model={model} />)
      .with('distribution', () => (
        <>
          <DistributionBlock model={model} />
          <AvgBuyMcDistributionBlock model={model} />
        </>
      ))
      .with('profit', () => <AnalysisBlock model={model} />)
      .exhaustive()

  return (
    <>
      <Root mode="always" variant="vertical" inlineStart={16} inlineEnd={16}>
        <PaddedGroup>
          {profileOwner === ProfileOwner.CurrentUser ? (
            <MyProfileHeader />
          ) : (
            <UserProfileHeader />
          )}
          <ProfileBoardStatisticsHeader model={model} />
          <PnlChart model={model} owner={profileOwner} />
        </PaddedGroup>
        <StatisticsTabs value={tab} onChange={setTab}>
          <UnderlineTabs.Tab value="phishing">
            {t('profile.statistics.tab-phishing')}
          </UnderlineTabs.Tab>
          <UnderlineTabs.Tab value="distribution">
            {t('profile.statistics.tab-distribution')}
          </UnderlineTabs.Tab>
          <UnderlineTabs.Tab value="profit">
            {t('profile.statistics.tab-profit')}
          </UnderlineTabs.Tab>
        </StatisticsTabs>
        <PaddedGroup>{renderScreen()}</PaddedGroup>
        <Tables delugeId={delugeId} wallets={wallets} />
      </Root>
      <DistributeDrawerMobile delugeId={delugeId} period={period} />
    </>
  )
}

const Root = styled(FadeOverflow)`
  display: grid;
  gap: 10px;
  position: relative;
  overflow: auto;
  height: 100%;
  min-width: 0;

  padding-block-start: 12px;
`

const PnlChart = styled(UserProfilePnl)`
  height: 325px;
`

const Tables = styled(StatisticsTables)`
  border-radius: 0;
  margin-top: 4px;
`

const PaddedGroup = styled.div`
  padding-inline: 12px;
  display: grid;
  gap: 14px;
`

const StatisticsTabs = styled(UnderlineTabs<ProfileStatisticsTab>)`
  padding-inline: 12px;
  border-bottom: 2px solid var(--appearance-1);
  display: flex;

  button {
    margin-bottom: -2px;
  }
`
